import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface LocationResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: string;
  city?: string;
  country?: string;
  formatted: string;
}

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private readonly NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
  private httpWithoutInterceptor: HttpClient;

  constructor(private http: HttpClient, private httpBackend: HttpBackend) {
    this.httpWithoutInterceptor = new HttpClient(httpBackend);
  }

  searchLocations(query: string): Observable<LocationResult[]> {
    if (!query || query.length < 3) {
      return of([]);
    }

    const params = {
      q: query,
      format: 'json',
      limit: '10',
      addressdetails: '1',
      extratags: '1',
      'accept-language': 'en',
      countrycodes: '',
      featuretype: 'city',
    };

    return this.httpWithoutInterceptor
      .get<any[]>(this.NOMINATIM_URL, { params })
      .pipe(
        map((results) => this.formatResults(results)),
        catchError((error) => {
          console.error('Location search error:', error);
          return of([]);
        })
      );
  }

  private formatResults(results: any[]): LocationResult[] {
    return results.map((result) => {
      const address = result.address || {};

      const city =
        address.city || address.town || address.village || address.municipality;
      const state = address.state || address.province;
      const country = address.country;

      let formatted = '';
      if (city) {
        formatted = city;
        if (state && state !== city) {
          formatted += `, ${state}`;
        }
        if (country) {
          formatted += `, ${country}`;
        }
      } else {
        formatted = result.display_name;
      }

      if (!formatted || formatted.trim() === '') {
        formatted = result.display_name || 'Unknown Location';
      }

      return {
        display_name: result.display_name,
        lat: result.lat,
        lon: result.lon,
        place_id: result.place_id,
        city: city,
        country: country,
        formatted: formatted,
      };
    });
  }
}
